// ============================================================
// PlatformTrainingTypesPage.js
// Route /platform/training-types — CareComply reference catalogue.
// Schema: Code, Title, HasExpiry, DefaultValidityMonths, SortOrder,
// IsActive. No Description column.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import Switch from '../components/Switch';
import Modal from '../components/Modal';
import Field from '../components/Field';
import { toast } from '../components/Toaster';
import {
  listPlatformTrainingTypes, createPlatformTrainingType, patchPlatformTrainingType,
} from '../api/adminPlatform';
import { translateError } from '../utils/errorMessages';
import './PlatformPages.css';

const BLANK_FORM = { code: '', title: '', hasExpiry: true, defaultValidityMonths: 12, sortOrder: 0 };

export default function PlatformTrainingTypesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingId, setPendingId] = useState(null);

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(BLANK_FORM);
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await listPlatformTrainingTypes();
      setItems(res.items || []);
    } catch (err) { setError(translateError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchData(); }, [fetchData]);

  function openAdd() {
    setEditingId(null); setForm(BLANK_FORM); setFormError(null); setIsFormOpen(true);
  }
  function openEdit(row) {
    setEditingId(row.trainingTypeId);
    setForm({
      code: row.code,
      title: row.title,
      hasExpiry: row.hasExpiry,
      defaultValidityMonths: row.defaultValidityMonths || 12,
      sortOrder: row.sortOrder,
    });
    setFormError(null);
    setIsFormOpen(true);
  }
  function closeForm() {
    if (submitting) return;
    setIsFormOpen(false); setEditingId(null); setForm(BLANK_FORM);
  }

  async function submitForm(e) {
    e.preventDefault();
    setFormError(null); setSubmitting(true);
    try {
      const payload = { ...form };
      // hasExpiry=false: send null months so it's properly cleared server-side.
      if (!payload.hasExpiry) payload.defaultValidityMonths = null;
      if (editingId) {
        const { code: _omit, ...patch } = payload;
        await patchPlatformTrainingType(editingId, patch);
        toast.success('Training type updated');
      } else {
        await createPlatformTrainingType(payload);
        toast.success('Training type created');
      }
      setIsFormOpen(false); setEditingId(null); setForm(BLANK_FORM);
      await fetchData();
    } catch (err) { setFormError(translateError(err)); }
    finally { setSubmitting(false); }
  }

  async function toggleActive(row, nextActive) {
    setPendingId(row.trainingTypeId);
    try {
      const result = await patchPlatformTrainingType(row.trainingTypeId, { isActive: nextActive });
      if (result && result.wasIdempotent) return;
      toast.success(nextActive ? 'Training type activated' : 'Training type deactivated');
      await fetchData();
    } catch (err) { toast.error(translateError(err)); }
    finally { setPendingId(null); }
  }

  const columns = [
    { key: 'code',  label: 'Code',  render: (r) => <code className="platform-page__code">{r.code}</code>, width: '200px' },
    { key: 'title', label: 'Title', render: (r) => r.title },
    { key: 'expiry', label: 'Default Expiry', width: '160px',
      render: (r) => r.hasExpiry ? `${r.defaultValidityMonths || '—'} months` : <span style={{ color: '#888' }}>No expiry</span>,
    },
    { key: 'sort', label: 'Sort Order', render: (r) => r.sortOrder, width: '100px', align: 'right' },
    { key: 'active', label: 'Status', width: '100px',
      render: (r) => (
        <Switch
          checked={r.isActive}
          onChange={(next) => toggleActive(r, next)}
          disabled={pendingId === r.trainingTypeId}
          ariaLabel={`${r.code} active`}
        />
      ),
    },
    { key: 'edit', label: '', width: '80px',
      render: (r) => (
        <button type="button" className="platform-page__row-action" onClick={() => openEdit(r)}>
          Edit
        </button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Training Types"
        subtitle="CareComply platform reference. Per-client overrides on each client's Platform Config tab."
        cta={(
          <button type="button" className="platform-page__cta" onClick={openAdd}>
            <Plus size={16} /> Add Training Type
          </button>
        )}
      />

      <DataTable
        data={items}
        columns={columns}
        rowKey={(r) => r.trainingTypeId}
        loading={loading}
        error={error}
        onRetry={fetchData}
        totalCount={items.length}
        page={1}
        pageSize={items.length || 1}
        onPageChange={() => {}}
        emptyMessage="No training types defined."
      />

      <Modal isOpen={isFormOpen} onClose={closeForm} title={editingId ? 'Edit training type' : 'Add training type'} closeDisabled={submitting}>
        <form onSubmit={submitForm} className="platform-page__form">
          <Field label="Code" required help="Lower-snake-case, 3-50 chars. Immutable after creation.">
            <input
              type="text"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value })}
              disabled={Boolean(editingId)}
              maxLength={50}
              required
              pattern="^[a-z0-9_]{3,50}$"
            />
          </Field>
          <Field label="Title" required>
            <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} maxLength={200} required />
          </Field>
          <Field label="Has expiry">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Switch
                checked={form.hasExpiry}
                onChange={(next) => setForm({ ...form, hasExpiry: next })}
                ariaLabel="Has expiry"
              />
              <span style={{ fontSize: 13 }}>{form.hasExpiry ? 'Training has an expiry date' : 'Once completed, never expires'}</span>
            </label>
          </Field>
          {form.hasExpiry && (
            <Field label="Default validity (months)" required help="1-120">
              <input
                type="number"
                min={1}
                max={120}
                value={form.defaultValidityMonths || ''}
                onChange={(e) => setForm({ ...form, defaultValidityMonths: parseInt(e.target.value, 10) || null })}
                required
              />
            </Field>
          )}
          <Field label="Sort Order">
            <input
              type="number" min={0} max={9999}
              value={form.sortOrder}
              onChange={(e) => setForm({ ...form, sortOrder: parseInt(e.target.value, 10) || 0 })}
            />
          </Field>
          {formError && <p className="platform-page__form-error" role="alert">{formError}</p>}
          <div className="platform-page__form-actions">
            <button type="button" onClick={closeForm} disabled={submitting} className="platform-page__btn-secondary">Cancel</button>
            <button type="submit" disabled={submitting} className="platform-page__btn-primary">
              {submitting ? 'Saving…' : (editingId ? 'Save' : 'Create')}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
